/* ── FAQ Problem Solver: topic carousel, collapsible topic groups + question
   accordion, quick-question jump, and search interactions. ──────────────── */
(() => {
  const initSection = (section) => {
    if (section.dataset.fpsInit === 'true') return;
    section.dataset.fpsInit = 'true';

    const groups = Array.from(section.querySelectorAll('[data-fps-topic-group]'));
    const items = Array.from(section.querySelectorAll('[data-fps-key]'));
    const topicCards = Array.from(section.querySelectorAll('[data-fps-filter]'));
    const quickCards = Array.from(section.querySelectorAll('[data-fps-target]'));
    const searchInput = section.querySelector('[data-fps-search-input]');
    const searchBtn = section.querySelector('[data-fps-search-btn]');
    const exampleButtons = Array.from(section.querySelectorAll('[data-fps-example]'));
    const track = section.querySelector('[data-fps-topic-track]');
    const prevBtn = section.querySelector('[data-fps-topic-prev]');
    const nextBtn = section.querySelector('[data-fps-topic-next]');

    /* ── Topic group accordion (single-open, but closable to none) ── */
    const openGroup = (group) => {
      groups.forEach((g) => {
        const isTarget = g === group;
        g.classList.toggle('is-open', isTarget);
        const toggle = g.querySelector('[data-fps-group-toggle]');
        if (toggle) toggle.setAttribute('aria-expanded', isTarget ? 'true' : 'false');
      });
    };

    groups.forEach((group) => {
      const toggle = group.querySelector('[data-fps-group-toggle]');
      if (!toggle) return;
      toggle.addEventListener('click', () => {
        const isOpen = group.classList.contains('is-open');
        openGroup(isOpen ? null : group);
      });
    });

    /* ── Individual question accordion (within a group) ── */
    section.querySelectorAll('[data-fps-toggle]').forEach((toggle) => {
      toggle.addEventListener('click', () => {
        const item = toggle.closest('[data-fps-key]');
        if (!item) return;
        const isOpen = item.classList.toggle('is-open');
        toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      });
    });

    /* ── Topic card → open matching group ── */
    const setActiveCard = (filter) => {
      topicCards.forEach((card) => {
        card.classList.toggle('is-active', card.dataset.fpsFilter === filter);
      });
    };

    topicCards.forEach((card) => {
      card.addEventListener('click', (e) => {
        // "View all" is an <a> with an optional real URL — only treat it as
        // an in-page action when no real link was configured.
        if (card.tagName === 'A' && card.getAttribute('href') && card.getAttribute('href') !== '#') return;
        e.preventDefault();

        const filter = card.dataset.fpsFilter;

        if (filter === 'all') {
          if (searchInput) searchInput.value = '';
          items.forEach((item) => { item.hidden = false; });
          groups.forEach((group) => { group.hidden = false; });
          openGroup(groups[0] || null);
          setActiveCard(groups[0] ? groups[0].dataset.fpsTopicGroup : '');
          if (groups[0]) groups[0].scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }

        const group = groups.find((g) => g.dataset.fpsTopicGroup === filter);
        if (!group) return;
        openGroup(group);
        setActiveCard(filter);
        group.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });

    /* ── Quick question → jump to its question, inside its group ── */
    quickCards.forEach((card) => {
      card.addEventListener('click', () => {
        const target = items.find((item) => item.dataset.fpsKey === card.dataset.fpsTarget);
        if (!target) return;

        const group = target.closest('[data-fps-topic-group]');
        openGroup(group);
        setActiveCard(group ? group.dataset.fpsTopicGroup : '');

        items.forEach((item) => {
          const isTarget = item === target;
          item.classList.toggle('is-open', isTarget);
          const toggle = item.querySelector('[data-fps-toggle]');
          if (toggle) toggle.setAttribute('aria-expanded', isTarget ? 'true' : 'false');
        });

        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    });

    /* ── Search across all groups ── */
    const runSearch = (value) => {
      const query = value.toLowerCase().trim();

      if (!query) {
        items.forEach((item) => { item.hidden = false; });
        groups.forEach((group) => { group.hidden = false; });
        openGroup(groups[0] || null);
        setActiveCard(groups[0] ? groups[0].dataset.fpsTopicGroup : '');
        return;
      }

      setActiveCard('');

      groups.forEach((group) => {
        const groupItems = Array.from(group.querySelectorAll('[data-fps-key]'));
        let groupHasMatch = false;

        groupItems.forEach((item) => {
          const matches = item.textContent.toLowerCase().includes(query);
          item.hidden = !matches;
          if (matches) groupHasMatch = true;
        });

        group.hidden = !groupHasMatch;
        if (groupHasMatch) {
          group.classList.add('is-open');
          const toggle = group.querySelector('[data-fps-group-toggle]');
          if (toggle) toggle.setAttribute('aria-expanded', 'true');
        }
      });
    };

    if (searchInput) {
      searchInput.addEventListener('input', (e) => runSearch(e.target.value));
    }

    if (searchBtn && searchInput) {
      searchBtn.addEventListener('click', () => runSearch(searchInput.value));
    }

    exampleButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        if (!searchInput) return;
        searchInput.value = btn.textContent;
        runSearch(searchInput.value);
        searchInput.focus();
      });
    });

    /* ── Topic carousel nav (only rendered once there are 7+ topics) ── */
    if (track && prevBtn && nextBtn) {
      const scrollByCard = (direction) => {
        const card = track.querySelector('.fps-topic-card');
        const amount = card ? card.getBoundingClientRect().width + 16 : track.clientWidth * 0.8;
        track.scrollBy({ left: amount * direction, behavior: 'smooth' });
      };
      prevBtn.addEventListener('click', () => scrollByCard(-1));
      nextBtn.addEventListener('click', () => scrollByCard(1));
    }
  };

  const initAll = () => {
    document.querySelectorAll('.fps-section').forEach(initSection);
  };

  initAll();
  document.addEventListener('shopify:section:load', initAll);
})();
